import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, Loader2, Plus, Trash2, Save, X, Eye, EyeOff,
  Award, Megaphone, Shield, GripVertical, ChevronDown, ChevronUp, Pencil,
  Upload, ImageIcon, Library
} from 'lucide-react';
import { MultilingualInput } from '../../components/MultilingualInput';
import { getLocalizedField, computeFieldCompleteness } from '../../context/LanguageContext';
import api from '../../utils/api';
import { MediaLibraryModal } from '../../components/cms/MediaPicker';

const getToken = () => '';

const BLOCK_TYPES = [
  { value: 'hero_content', label: 'Hero Content', icon: Megaphone, color: 'bg-purple-100 text-purple-700' },
  // TREATX-OWNER-UAT-LANDING-CMS-RETIRE-CERT-01:
  // The `certification` block_type is retired here — the canonical
  // certificate SSOT is `treatx_corporate_certificates` (managed at
  // /treatx-admin/corporate-identity). Rows that still exist in
  // `landing_content_blocks` are preserved for audit but are no
  // longer editable through this UI and no longer render publicly.
  { value: 'announcement', label: 'Announcement', icon: Megaphone, color: 'bg-amber-100 text-amber-700' },
  { value: 'trust_content', label: 'Trust Content', icon: Shield, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'feature_content', label: 'Feature Block', icon: Shield, color: 'bg-indigo-100 text-indigo-700' },
  { value: 'section_copy', label: 'Section Copy', icon: Megaphone, color: 'bg-sky-100 text-sky-700' },
];

// TREATX-LANDING-V3 — the V3 landing reads these keys to place a block. The
// page keeps a safe built-in default for every section, so an unmanaged
// section still renders correctly.
const V3_SECTION_KEYS = [
  { value: 'hero', label: 'Hero' },
  { value: 'how_it_works', label: 'How it works' },
  { value: 'treatments', label: 'Treatments' },
  { value: 'providers', label: 'Providers' },
  { value: 'showcase', label: 'Product showcase' },
  { value: 'why', label: 'Why TreatX' },
  { value: 'certifications', label: 'Certifications' },
  { value: 'provider_cta', label: 'For providers' },
  { value: 'academy', label: 'Academy' },
  { value: 'apps', label: 'Mobile apps' },
  { value: 'final_cta', label: 'Final call to action' },
];

const typeConfig = (bt) => BLOCK_TYPES.find(b => b.value === bt) || BLOCK_TYPES[0];

// ── Image Upload Component ────────────────────────────────
export const ImageUploader = ({ currentUrl, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(currentUrl || '');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileRef = React.useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Only JPG, PNG, and WebP files are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/landing-content/upload-image', formData, {
        headers: { 'Content-Type': undefined },
      });
      const data = res.data;
      if (data.image_url) {
        setPreview(data.thumbnail_url || data.image_url);
        onUploaded({
          image_url: data.image_url,
          thumbnail_url: data.thumbnail_url,
          compressed_url: data.compressed_url,
          image_storage_key: data.image_storage_key,
          image_thumbnail_key: data.image_thumbnail_key,
          image_compressed_key: data.image_compressed_key,
        });
      } else {
        alert(data.detail || data.error || 'Upload failed');
      }
    } catch { alert('Upload failed'); }
    setUploading(false);
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files[0]); };

  return (
    <div className="col-span-2">
      <Label className="text-xs">Image</Label>
      <div
        className={`mt-1 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-[#3BA4C5] bg-[#3BA4C5]/5' : 'border-slate-200 hover:border-slate-300'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        data-testid="image-dropzone"
      >
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
          onChange={(e) => handleUpload(e.target.files[0])} data-testid="image-file-input" />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 py-2">
            <Loader2 className="h-4 w-4 animate-spin text-[#3BA4C5]" />
            <span className="text-xs text-slate-500">Uploading & processing...</span>
          </div>
        ) : preview ? (
          <div className="flex items-center gap-3">
            <img src={preview} alt="Preview" className="h-12 w-12 rounded object-cover border" data-testid="image-preview" 
              onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="text-left flex-1">
              <p className="text-xs text-slate-600">Image uploaded</p>
              <p className="text-[10px] text-slate-400">Click or drag to replace</p>
            </div>
          </div>
        ) : (
          <div className="py-2">
            <Upload className="h-5 w-5 text-slate-400 mx-auto mb-1" />
            <p className="text-xs text-slate-500">Drop image here or click to browse</p>
            <p className="text-[10px] text-slate-400">JPG, PNG, WebP — Max 5MB</p>
          </div>
        )}
      </div>
      {/* Reuses the canonical CMS media library (/api/admin/media/library) so an
          already-uploaded S3 asset can be chosen instead of re-uploading it. The
          dropzone above stays the primary path because it ALSO generates the
          thumbnail/compressed variants — this adds selection, not a second
          uploader. */}
      <div className="mt-1 flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
          onClick={() => setLibraryOpen(true)} data-testid="image-choose-existing">
          <Library className="h-3 w-3 mr-1" /> Choose existing
        </Button>
        {preview && (
          <Button type="button" variant="ghost" size="sm"
            className="h-7 text-xs text-rose-600 hover:bg-rose-50"
            onClick={() => {
              setPreview('');
              onUploaded({
                image_url: '', image_storage_key: '',
                image_thumbnail_key: '', image_compressed_key: '',
              });
            }}
            data-testid="image-remove">
            <Trash2 className="h-3 w-3 mr-1" /> Remove
          </Button>
        )}
      </div>
      <MediaLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={(url) => { setPreview(url); onUploaded({ image_url: url }); }}
        testId="landing-cms"
      />
    </div>
  );
};

// ── Block Form ────────────────────────────────────────────
const BlockForm = ({ block, onSave, onCancel, saving }) => {
  const [form, setForm] = useState({
    block_type: block?.block_type || 'certification',
    title: block?.title || { en: '', tr: '', de: '', es: '', ar: '', ru: '', fr: '' },
    description: block?.description || { en: '', tr: '', de: '', es: '', ar: '', ru: '', fr: '' },
    image_url: block?.image_url || '',
    image_storage_key: block?.image_storage_key || '',
    image_thumbnail_key: block?.image_thumbnail_key || '',
    image_compressed_key: block?.image_compressed_key || '',
    thumbnail_url: block?.thumbnail_url || '',
    compressed_url: block?.compressed_url || '',
    button_text: block?.button_text || '',
    button_link: block?.button_link || '',
    display_order: block?.display_order ?? 0,
    active: block?.active ?? true,
    start_date: block?.start_date || '',
    end_date: block?.end_date || '',
    // TREATX-LANDING-V3 — which homepage version(s) and surface(s) this block
    // belongs to. Empty = legacy scope (V1/V2) and all surfaces, so existing
    // records keep behaving exactly as they do today.
    homepage_versions: block?.homepage_versions || [],
    surfaces: block?.surfaces || [],
    section_key: block?.section_key || '',
  });

  const isEdit = !!block?.block_id;
  const isAnnouncement = form.block_type === 'announcement';
  const toggleIn = (key, value) => setForm(f => ({
    ...f,
    [key]: (f[key] || []).includes(value)
      ? (f[key] || []).filter(x => x !== value)
      : [...(f[key] || []), value],
  }));

  return (
    <Card className="border-2 border-[#3BA4C5]/30 bg-white" data-testid="block-form">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#122033]">{isEdit ? 'Edit Block' : 'New Block'}</h3>
          <Button variant="ghost" size="sm" onClick={onCancel} className="h-7 w-7 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {!isEdit && (
          <div>
            <Label className="text-xs">Block Type</Label>
            <div className="flex gap-2 mt-1">
              {BLOCK_TYPES.map(bt => (
                <button
                  key={bt.value}
                  onClick={() => setForm(f => ({ ...f, block_type: bt.value }))}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    form.block_type === bt.value ? 'bg-[#122033] text-white border-[#122033]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`type-${bt.value}`}
                >
                  {bt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <MultilingualInput
              value={form.title}
              onChange={(val) => setForm(f => ({ ...f, title: val }))}
              label="Title"
              required
              placeholder="Block title"
              policy="translatable_required"
            />
          </div>
          <div className="col-span-2">
            <MultilingualInput
              value={form.description}
              onChange={(val) => setForm(f => ({ ...f, description: val }))}
              label="Description"
              multiline
              placeholder="Description text"
              policy="display_fallback_only"
            />
          </div>
          <ImageUploader
            currentUrl={form.thumbnail_url || form.image_url}
            onUploaded={(imgs) => setForm(f => ({ ...f, ...imgs }))}
          />
          <div>
            <Label className="text-xs text-slate-400">Image URL — advanced / existing records</Label>
            <Input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
              placeholder="https://..." className="h-8 text-sm" data-testid="block-image-url-input" />
          </div>
          <div>
            <Label className="text-xs">Display Order</Label>
            <Input type="number" value={form.display_order} onChange={e => setForm(f => ({ ...f, display_order: parseInt(e.target.value) || 0 }))}
              className="h-8 text-sm" data-testid="block-order-input" />
          </div>
          {isAnnouncement && (
            <>
              <div>
                <Label className="text-xs">Button Text</Label>
                <Input value={form.button_text} onChange={e => setForm(f => ({ ...f, button_text: e.target.value }))}
                  placeholder="Learn More" className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Button Link</Label>
                <Input value={form.button_link} onChange={e => setForm(f => ({ ...f, button_link: e.target.value }))}
                  placeholder="/find-clinics" className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input type="datetime-local" value={form.start_date?.slice(0, 16) || ''} onChange={e => setForm(f => ({ ...f, start_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                  className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">End Date</Label>
                <Input type="datetime-local" value={form.end_date?.slice(0, 16) || ''} onChange={e => setForm(f => ({ ...f, end_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                  className="h-8 text-sm" />
              </div>
            </>
          )}
        </div>

        {/* TREATX-LANDING-V3 — scope. No migration: an empty selection keeps a
            block on the legacy homepage and on every surface. */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2"
             data-testid="block-scope">
          <div>
            <Label className="text-xs">Homepage versions</Label>
            <div className="flex gap-2 mt-1">
              {['V1', 'V3'].map(v => (
                <button key={v} onClick={() => toggleIn('homepage_versions', v)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    (form.homepage_versions || []).includes(v)
                      ? 'bg-[#122033] text-white border-[#122033]'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`block-version-${v.toLowerCase()}`}>{v}</button>
              ))}
              <span className="text-[10px] text-slate-500 self-center">
                none selected = legacy homepage (V1/V2)
              </span>
            </div>
          </div>
          <div>
            <Label className="text-xs">Surfaces</Label>
            <div className="flex gap-2 mt-1">
              {['GLOBAL', 'TR'].map(sf => (
                <button key={sf} onClick={() => toggleIn('surfaces', sf)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    (form.surfaces || []).includes(sf)
                      ? 'bg-[#122033] text-white border-[#122033]'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`block-surface-${sf.toLowerCase()}`}>{sf}</button>
              ))}
              <span className="text-[10px] text-slate-500 self-center">
                none selected = all surfaces
              </span>
            </div>
          </div>
          <div>
            <Label className="text-xs">Section (V3)</Label>
            <select value={form.section_key}
              onChange={e => setForm(f => ({ ...f, section_key: e.target.value }))}
              className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              data-testid="block-section-key">
              <option value="">— not section specific —</option>
              {V3_SECTION_KEYS.map(k => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
              className="rounded" data-testid="block-active-toggle" />
            Active
          </label>
          <Button size="sm" onClick={() => onSave(form)} disabled={saving || !(typeof form.title === 'string' ? form.title.trim() : form.title?.en?.trim())}
            className="h-7 text-xs bg-[#3BA4C5] hover:bg-[#2d8aab]" data-testid="block-save-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Block Card ────────────────────────────────────────────
const BlockCard = ({ block, onEdit, onDelete, onToggle, deleting }) => {
  const cfg = typeConfig(block.block_type);
  const Icon = cfg.icon;
  const imgUrl = block.thumbnail_url || block.compressed_url || block.image_url;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${block.active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`}
      data-testid={`block-card-${block.block_id}`}>
      <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
      {imgUrl ? (
        <img src={imgUrl} alt="" className="h-8 w-8 rounded-lg object-cover shrink-0 border"
          onError={(e) => { e.target.style.display = 'none'; }} data-testid={`block-img-${block.block_id}`} />
      ) : (
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${cfg.color}`}>
          <Icon className="h-4 w-4" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-[#122033] truncate">{getLocalizedField(block.title, 'en')}</p>
          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${cfg.color}`}>{cfg.label}</Badge>
          {/* TREATX-V3-CMS-GAP-01 — which homepage a record belongs to must be
              visible at a glance, so V1 and V3 copy are never confused. */}
          <Badge variant="outline"
                 className={`text-[9px] px-1.5 py-0 ${(block.homepage_versions || []).includes('V3')
                   ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}`}
                 data-testid={`block-scope-badge-${block.block_id}`}>
            {(block.homepage_versions || []).length
              ? (block.homepage_versions || []).join(' + ')
              : 'V1 legacy'}
          </Badge>
          {block.section_key && (
            <span className="text-[9px] text-slate-400">{block.section_key}</span>
          )}
          <span className="text-[9px] text-slate-400">#{block.display_order}</span>
          {(() => {
            const comp = computeFieldCompleteness(block.title);
            const color = comp.filled === comp.total ? 'text-green-600 bg-green-50'
              : comp.filled > 1 ? 'text-amber-600 bg-amber-50' : 'text-blue-600 bg-blue-50';
            return <span className={`text-[9px] px-1 py-0 rounded ${color}`}>{comp.filled}/{comp.total}</span>;
          })()}
        </div>
        {block.description && <p className="text-xs text-slate-500 truncate">{getLocalizedField(block.description, 'en')}</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onToggle(block)}
          data-testid={`toggle-${block.block_id}`}>
          {block.active ? <Eye className="h-3.5 w-3.5 text-emerald-500" /> : <EyeOff className="h-3.5 w-3.5 text-slate-400" />}
        </Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(block)}
          data-testid={`edit-${block.block_id}`}>
          <Pencil className="h-3.5 w-3.5 text-slate-500" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:text-red-500" onClick={() => onDelete(block.block_id)}
          disabled={deleting} data-testid={`delete-${block.block_id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────
export default function AdminLandingCMS({ embedded = false }) {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editBlock, setEditBlock] = useState(null); // null = closed, {} = new, {block_id: ...} = edit
  const [filter, setFilter] = useState('all');

  const fetchBlocks = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?block_type=${filter}` : '';
      const res = await api.get(`/admin/landing-content${params}`);
      // TREATX-V3-CMS-GAP-01 — this panel is the V1 homepage. Records scoped to
      // the V3 candidate are edited on the "Homepage (V3 — candidate)" tab, so
      // they are not mixed in here; nothing is deleted or rescoped.
      setBlocks((res.data.blocks || []).filter(
        (b) => !(b.homepage_versions || []).includes('V3')));
    } catch { /* silent */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchBlocks(); }, [fetchBlocks]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const isEdit = !!editBlock?.block_id;
      if (isEdit) {
        await api.put(`/admin/landing-content/${editBlock.block_id}`, form);
      } else {
        await api.post('/admin/landing-content', form);
      }
      setEditBlock(null);
      await fetchBlocks();
    } catch (err) {
      console.error('[CMS] Save failed:', err);
      alert(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (blockId) => {
    if (!window.confirm('Delete this content block?')) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/landing-content/${blockId}`);
      await fetchBlocks();
    } finally {
      setDeleting(false);
    }
  };

  const handleToggle = async (block) => {
    try {
      await api.put(`/admin/landing-content/${block.block_id}`, { active: !block.active });
      await fetchBlocks();
    } catch { /* silent */ }
  };

  const certBlocks = blocks.filter(b => b.block_type === 'certification');
  const announceBlocks = blocks.filter(b => b.block_type === 'announcement');
  const trustBlocks = blocks.filter(b => b.block_type === 'trust_content');
  const heroBlocks = blocks.filter(b => b.block_type === 'hero_content');
  // TREATX-V3-CMS-GAP-01 — these two types existed in the model and in the
  // public readers but were never LISTED here, so a record the owner (or this
  // panel) created was invisible and therefore unmanageable. They are listed
  // now; the section-by-section V3 editor is the friendlier surface for the
  // same records.
  const sectionCopyBlocks = blocks.filter(b => b.block_type === 'section_copy');
  const featureBlocks = blocks.filter(b => b.block_type === 'feature_content' && !b.scene_key);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="landing-cms-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BA4C5]" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50'} data-testid="admin-landing-cms">
      <div className={embedded ? '' : 'max-w-4xl mx-auto px-4 py-6'}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {!embedded && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin/dashboard')}
              className="h-8 w-8 p-0" data-testid="cms-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex-1">
            {!embedded && (
              <>
                <h1 className="text-lg font-bold text-[#122033]">Landing Page CMS</h1>
                <p className="text-xs text-slate-400">Manage landing page content blocks</p>
              </>
            )}
          </div>
          <Button size="sm" onClick={() => setEditBlock({})} className="h-8 gap-1 text-xs bg-[#3BA4C5] hover:bg-[#2d8aab]"
            data-testid="add-block-btn">
            <Plus className="h-3 w-3" /> Add Block
          </Button>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {[{ v: 'all', l: 'All' }, ...BLOCK_TYPES.map(b => ({ v: b.value, l: b.label }))].map(f => (
            <button key={f.v}
              className={`text-xs px-2.5 py-1 rounded-full transition-colors ${filter === f.v ? 'bg-[#122033] text-white' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'}`}
              onClick={() => setFilter(f.v)} data-testid={`filter-${f.v}`}>
              {f.l}
            </button>
          ))}
          <span className="text-[10px] text-slate-400 ml-2">{blocks.length} V1 blocks</span>
        </div>

        <p className="mb-4 text-[11px] text-slate-500" data-testid="cms-v3-pointer">
          Looking for the V3 candidate — its section copy, buttons and product
          screenshots? They are all on the
          <span className="font-medium"> Homepage (V3 — candidate)</span> tab.
        </p>

        {/* Edit/Create Form */}
        {editBlock !== null && (
          <div className="mb-4">
            <BlockForm block={editBlock} onSave={handleSave} onCancel={() => setEditBlock(null)} saving={saving} />
          </div>
        )}

        {/* Hero Content Section */}
        {(filter === 'all' || filter === 'hero_content') && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Megaphone className="h-4 w-4 text-purple-500" />
              <h2 className="text-sm font-semibold text-[#122033]">Hero Content</h2>
              <Badge variant="outline" className="text-[10px]">{heroBlocks.length}</Badge>
            </div>
            <div className="space-y-1.5" data-testid="hero-blocks-list">
              {heroBlocks.map(b => (
                <BlockCard key={b.block_id} block={b} onEdit={setEditBlock} onDelete={handleDelete}
                  onToggle={handleToggle} deleting={deleting} />
              ))}
              {heroBlocks.length === 0 && <p className="text-xs text-slate-400 py-2">No hero content blocks</p>}
            </div>
          </div>
        )}

        {/* Certifications Section — RETIRED (Owner UAT §1).
            The `certification` block_type is no longer surfaced to the
            Owner in this Landing CMS. Historical rows in
            `landing_content_blocks` remain physically stored (audit
            trail preserved) but are neither editable here nor rendered
            on any public surface. The canonical writable Certificate
            authority is `treatx_corporate_certificates`, managed at
            /treatx-admin/corporate-identity. */}
        {false /* certification block-type hidden from Landing CMS */ && (filter === 'all' || filter === 'certification') && certBlocks.length > 0 && (
          <div className="mb-6" />
        )}

        {/* Announcements Section */}
        {(filter === 'all' || filter === 'announcement') && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Megaphone className="h-4 w-4 text-amber-500" />
              <h2 className="text-sm font-semibold text-[#122033]">Announcements</h2>
              <Badge variant="outline" className="text-[10px]">{announceBlocks.length}</Badge>
            </div>
            <div className="space-y-1.5" data-testid="announce-blocks-list">
              {announceBlocks.map(b => (
                <BlockCard key={b.block_id} block={b} onEdit={setEditBlock} onDelete={handleDelete}
                  onToggle={handleToggle} deleting={deleting} />
              ))}
              {announceBlocks.length === 0 && <p className="text-xs text-slate-400 py-2">No announcement blocks</p>}
            </div>
          </div>
        )}

        {/* Trust Content Section */}
        {(filter === 'all' || filter === 'trust_content') && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-emerald-500" />
              <h2 className="text-sm font-semibold text-[#122033]">Trust Content</h2>
              <Badge variant="outline" className="text-[10px]">{trustBlocks.length}</Badge>
            </div>
            <div className="space-y-1.5" data-testid="trust-blocks-list">
              {trustBlocks.map(b => (
                <BlockCard key={b.block_id} block={b} onEdit={setEditBlock} onDelete={handleDelete}
                  onToggle={handleToggle} deleting={deleting} />
              ))}
              {trustBlocks.length === 0 && <p className="text-xs text-slate-400 py-2">No trust content blocks</p>}
            </div>
          </div>
        )}

        {/* Section Copy — the per-section headings/descriptions a landing
            version reads. Listed here so no record type is invisible. */}
        {(filter === 'all' || filter === 'section_copy') && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Megaphone className="h-4 w-4 text-sky-500" />
              <h2 className="text-sm font-semibold text-[#122033]">Section Copy</h2>
              <Badge variant="outline" className="text-[10px]">{sectionCopyBlocks.length}</Badge>
            </div>
            <div className="space-y-1.5" data-testid="section-copy-blocks-list">
              {sectionCopyBlocks.map(b => (
                <BlockCard key={b.block_id} block={b} onEdit={setEditBlock} onDelete={handleDelete}
                  onToggle={handleToggle} deleting={deleting} />
              ))}
              {sectionCopyBlocks.length === 0 && (
                <p className="text-xs text-slate-400 py-2">
                  No section copy records — every section renders its built-in default.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Feature blocks (list items of a section; product visuals excluded —
            they are managed in the V3 Product Visuals panel). */}
        {(filter === 'all' || filter === 'feature_content') && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-indigo-500" />
              <h2 className="text-sm font-semibold text-[#122033]">Feature Blocks</h2>
              <Badge variant="outline" className="text-[10px]">{featureBlocks.length}</Badge>
            </div>
            <div className="space-y-1.5" data-testid="feature-blocks-list">
              {featureBlocks.map(b => (
                <BlockCard key={b.block_id} block={b} onEdit={setEditBlock} onDelete={handleDelete}
                  onToggle={handleToggle} deleting={deleting} />
              ))}
              {featureBlocks.length === 0 && <p className="text-xs text-slate-400 py-2">No feature blocks</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
